// src/components/network/ForfeoPartnerNetworkProgram.tsx
import React from 'react';

type ForfeoPartnerNetworkProgramProps = {
  variant?: 'public' | 'quality' | 'compact';
  className?: string;
};

const pillars = [
  {
    icon: '✓',
    title: 'Partenaire certifié',
    text: 'Un réseau local plus clair, avec des critères de qualité, de confiance et de suivi visibles pour les voyageurs.',
  },
  {
    icon: '▦',
    title: 'Wallet & QR sécurisé',
    text: 'Les expériences sont progressivement préparées pour les pass mobiles, les preuves d’achat et la validation terrain.',
  },
  {
    icon: '◈',
    title: 'Bundles & expériences',
    text: 'Forfeo peut mieux regrouper restaurants, hébergements, activités et offres locales dans des parcours cohérents.',
  },
  {
    icon: '↗',
    title: 'Croissance mesurable',
    text: 'Les partenaires peuvent suivre la visibilité, les ventes, les validations, la satisfaction et les opportunités régionales.',
  },
];

const qualitySignals = [
  'Expériences mieux présentées',
  'Suivi des réservations et achats',
  'Préparation au paiement partenaire',
  'Base pour les PRP et territoires',
];

export default function ForfeoPartnerNetworkProgram({
  variant = 'public',
  className = '',
}: ForfeoPartnerNetworkProgramProps) {
  const isCompact = variant === 'compact';

  return (
    <section
      className={`relative overflow-hidden rounded-[2rem] border border-blue-100 bg-gradient-to-br from-white via-sky-50 to-blue-50 p-5 shadow-[0_22px_70px_rgba(15,23,42,0.08)] md:p-8 ${className}`}
    >
      <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-cyan-300/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative z-10 grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white/80 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-blue-700 shadow-sm">
            <span className="inline-flex h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_18px_rgba(34,211,238,0.85)]" />
            Forfeo Network
          </div>

          <h2 className="text-2xl font-black tracking-tight text-slate-950 md:text-4xl">
            Un réseau local plus fiable pour vendre, réserver et valider les expériences.
          </h2>

          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">
            Forfeo Explore prépare les partenaires à un modèle plus structuré : expériences certifiées,
            wallet mobile, QR sécurisé, suivi des réservations et meilleure visibilité régionale.
          </p>

          {!isCompact && (
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              {qualitySignals.map((signal) => (
                <div
                  key={signal}
                  className="flex items-center gap-2 rounded-2xl border border-white/80 bg-white/80 px-3 py-2 text-sm font-bold text-slate-700 shadow-sm"
                >
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-50 text-xs font-black text-emerald-700">
                    ✓
                  </span>
                  {signal}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {pillars.map((pillar) => (
            <article
              key={pillar.title}
              className="rounded-3xl border border-white/80 bg-white/85 p-4 shadow-[0_14px_36px_rgba(15,23,42,0.07)] backdrop-blur"
            >
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-400 text-base font-black text-white shadow-lg">
                {pillar.icon}
              </div>
              <h3 className="text-sm font-black text-slate-950 md:text-base">{pillar.title}</h3>
              <p className="mt-2 text-xs leading-6 text-slate-600 md:text-sm">{pillar.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export { ForfeoPartnerNetworkProgram };
